import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { WorkflowStageType } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import {
  WORKFLOW_EMAIL_PLACEHOLDERS,
  type WorkflowFormInput,
} from '../schemas/workflow-form-schema';

/** Confirmation, file and review stages email on their own action; "when" does not apply. */
const OWN_EMAIL: readonly WorkflowStageType[] = [
  'customer_confirmation',
  'send_files',
  'customer_review',
];

export interface StageEmailFieldsProps {
  /** `stages.0` or `stages.0.subSteps.1`. */
  prefix: `stages.${number}` | `stages.${number}.subSteps.${number}`;
  type: WorkflowStageType;
  disabled?: boolean;
  /** Marks the first stage's email settings for the workflow editor tour. */
  tour?: boolean;
}

export function StageEmailFields({ prefix, type, disabled, tour }: StageEmailFieldsProps) {
  const { t } = useTranslation('workflows');
  const form = useFormContext<WorkflowFormInput>();
  const own = OWN_EMAIL.includes(type);
  const when = useWatch({ control: form.control, name: `${prefix}.emailWhen` });
  const showText = own || when !== 'none';

  const insert = (name: string) => {
    const field = `${prefix}.emailBody` as const;
    const current = form.getValues(field);
    const token = `{{${name}}}`;
    form.setValue(
      field,
      current ? `${current}${current.endsWith(' ') ? '' : ' '}${token}` : token,
      {
        shouldDirty: true,
      },
    );
  };

  return (
    <fieldset
      className="flex flex-col gap-3 rounded-md border p-3"
      data-tour={tour ? 'workflows.editor.stage.email' : undefined}
    >
      <legend className="px-1 text-sm font-medium">{t('email.title')}</legend>
      {own ? (
        <p className="text-muted-foreground text-xs">{t('email.ownEmail')}</p>
      ) : (
        <FormField
          control={form.control}
          name={`${prefix}.emailWhen`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('email.when')}</FormLabel>
              <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                <FormControl>
                  <SelectTrigger className="w-full sm:w-64">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {(['none', 'start', 'complete'] as const).map((option) => (
                    <SelectItem key={option} value={option}>
                      {t(`email.whenOptions.${option}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      {showText ? (
        <>
          <FormField
            control={form.control}
            name={`${prefix}.emailSubject`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('email.subject')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    maxLength={200}
                    placeholder={t('email.subjectPlaceholder')}
                    disabled={disabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`${prefix}.emailBody`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('email.body')}</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    rows={4}
                    placeholder={t('email.bodyPlaceholder')}
                    disabled={disabled}
                  />
                </FormControl>
                <FormDescription>{t('email.bodyHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-medium">{t('email.placeholders')}</p>
            <p className="text-muted-foreground text-xs">{t('email.placeholdersHint')}</p>
            <ul className="flex flex-wrap gap-1.5" aria-label={t('email.placeholders')}>
              {WORKFLOW_EMAIL_PLACEHOLDERS.map((name) => (
                <li key={name}>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 font-mono text-xs"
                    aria-label={t('email.insert', { name })}
                    disabled={disabled}
                    onClick={() => insert(name)}
                  >
                    {`{{${name}}}`}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </>
      ) : null}
    </fieldset>
  );
}
